import { _notNil, getJwtPayload, jwtValid } from '@/littledash.ts';
import { currentReturnTo, logout } from '@/support/auth.ts';
import { ApplicationEvents } from '@/utils/ApplicationEvents.ts';
import { type FC, useCallback, useEffect, useState } from 'react';

export const ExpiredJwtDetector: FC = () => {
  const [authenticated, updateAuthenticated] = useState(() => jwtValid());

  const checkJWT = useCallback(() => {
    const jwtPayload = getJwtPayload();
    if (_notNil(jwtPayload)) {
      if (jwtPayload.exp * 1000 < Date.now()) {
        logout({ type: 'session-expired', returnTo: currentReturnTo() });
      }
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'visible') {
          checkJWT();
        }
      },
      { signal: abortController.signal, passive: true }
    );
    window.addEventListener('online', () => checkJWT(), { signal: abortController.signal, passive: true });
    ApplicationEvents.onAuthenticated(() => updateAuthenticated(true), abortController.signal);

    return () => abortController.abort();
  }, [checkJWT, updateAuthenticated]);

  useEffect(() => {
    if (authenticated) {
      const exp = getJwtPayload()?.exp;
      if (Number.isFinite(exp)) {
        const timeout = (exp as number) * 1000 - Date.now() + 30000;
        if (timeout > 0) {
          const timeoutId = setTimeout(() => checkJWT(), timeout);
          return () => {
            clearTimeout(timeoutId);
          };
        } else {
          checkJWT();
        }
      }
    }
  }, [authenticated, checkJWT]);

  return null;
};
